import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker, Source, Layer } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Supercluster from "supercluster";
import { PlusIcon, MinusIcon, CrosshairSimpleIcon, CalendarBlankIcon } from "@phosphor-icons/react";
import { DIFFICULTY } from "@/lib/apiClient";
import { useDiscoverEvents } from "@/context/DiscoverEventsContext";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAP_STYLE = "mapbox://styles/mapbox/dark-v11";
const radians = Math.PI / 180;

function circlePolygon(lat, lng, radiusKm, points = 64) {
  const coords = [];
  const R = 6371;
  const latR = lat * radians;
  for (let i = 0; i <= points; i++) {
    const brng = (i * 360) / points * radians;
    const d = radiusKm / R;
    const lat2 = Math.asin(Math.sin(latR) * Math.cos(d) + Math.cos(latR) * Math.sin(d) * Math.cos(brng));
    const lng2 = lng * radians + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(latR), Math.cos(d) - Math.sin(latR) * Math.sin(lat2));
    coords.push([lng2 / radians, lat2 / radians]);
  }
  return { type: "Feature", geometry: { type: "Polygon", coordinates: [coords] } };
}

function eventWithinRadius(event, origin, radiusKm) {
  if (!Number.isFinite(event.latitude) || !Number.isFinite(event.longitude)) return false;
  if (!origin || !radiusKm) return true;
  const a = origin.lat * radians;
  const b = event.latitude * radians;
  const latDelta = b - a;
  const lngDelta = (event.longitude - origin.lng) * radians;
  const h = Math.sin(latDelta / 2) ** 2 + Math.cos(a) * Math.cos(b) * Math.sin(lngDelta / 2) ** 2;
  return 12742 * Math.asin(Math.min(1, Math.sqrt(h))) <= radiusKm;
}

export function MapView({
  parks = [], trails = null, origin, radiusKm, selectedId, hoveredId, onSelect,
  selectedParkOverlay, onHover, selectedTrailId, hoveredTrailId, focusedTrailId,
  trailFocusKey, onTrailHover, onTrailSelect, onViewportSettled, flyToCenter, flyToKey,
  fitKey, fitTrails = false, cluster = false, center = [50.5, 9.5], zoom = 6,
  minZoom = 4, className = "",
}) {
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [viewport, setViewport] = useState({ bounds: null, zoom });
  // Only the main Discover page provides this context. Other MapView consumers are unaffected.
  const discoverEvents = useDiscoverEvents();
  const setSearchArea = discoverEvents?.setSearchArea;
  const eventLayerVisible = discoverEvents?.visible === true;
  const selectedEventId = discoverEvents?.selectedEventId;
  const category = discoverEvents?.category;
  const focusEvent = discoverEvents?.focusEvent;
  const selectEvent = discoverEvents?.selectEvent;

  useEffect(() => {
    if (!setSearchArea) return;
    setSearchArea({ origin: origin ? { lat: origin.lat, lng: origin.lng } : null, radiusKm: radiusKm || null });
  }, [setSearchArea, origin, radiusKm]);

  const eventPins = useMemo(() => eventLayerVisible ? (discoverEvents?.events || []).filter((event) =>
    (event.id === selectedEventId || ((category === "all" || event.category === category) && eventWithinRadius(event, origin, radiusKm))) &&
    Number.isFinite(event.latitude) && Number.isFinite(event.longitude)
  ) : [], [eventLayerVisible, discoverEvents?.events, selectedEventId, category, origin, radiusKm]);
  const eventById = useMemo(() => new globalThis.Map(eventPins.map((event) => [event.id, event])), [eventPins]);

  const trailFC = useMemo(() => {
    if (!trails) return null;
    return { type: "FeatureCollection", features: trails.filter((trail) => trail.path && trail.path.length >= 2).map((trail) => ({
      type: "Feature", properties: { id: trail.id, color: DIFFICULTY[trail.difficulty]?.color || "#CCFF00" },
      geometry: { type: "LineString", coordinates: trail.path.map(([lat, lng]) => [lng, lat]) },
    })) };
  }, [trails]);
  const radiusFC = useMemo(() => (origin && radiusKm ? circlePolygon(origin.lat, origin.lng, radiusKm) : null), [origin, radiusKm]);

  const doFit = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    if (origin && radiusKm && !fitTrails) {
      const points = circlePolygon(origin.lat, origin.lng, radiusKm).geometry.coordinates[0];
      const bounds = points.reduce((current, point) => current.extend(point), new mapboxgl.LngLatBounds(points[0], points[0]));
      map.fitBounds(bounds, { padding: 96, maxZoom: 9, duration: 700 });
      return;
    }
    const points = [];
    if (fitTrails && trailFC) trailFC.features.forEach((feature) => feature.geometry.coordinates.forEach((point) => points.push(point)));
    else { parks.forEach((park) => points.push([park.lng, park.lat])); if (origin) points.push([origin.lng, origin.lat]); }
    if (points.length === 1) map.flyTo({ center: points[0], zoom: fitTrails ? 13 : 9, duration: 700 });
    else if (points.length > 1) {
      const bounds = points.reduce((current, point) => current.extend(point), new mapboxgl.LngLatBounds(points[0], points[0]));
      map.fitBounds(bounds, { padding: fitTrails ? 60 : 80, maxZoom: fitTrails ? 15 : 10, duration: 700 });
    }
  }, [fitTrails, trailFC, parks, origin, radiusKm]);

  const focusTrail = useCallback((trailId) => {
    const map = mapRef.current?.getMap?.();
    if (!map || !trailId || !trailFC) return;
    const feature = trailFC.features.find((candidate) => candidate.properties.id === trailId);
    const points = feature?.geometry?.coordinates ?? [];
    if (points.length === 1) map.flyTo({ center: points[0], zoom: 15, duration: 650 });
    else if (points.length > 1) {
      const bounds = points.reduce((current, point) => current.extend(point), new mapboxgl.LngLatBounds(points[0], points[0]));
      map.fitBounds(bounds, { padding: 90, maxZoom: 16, duration: 650 });
    }
  }, [trailFC]);

  const lastFitKey = useRef(null);
  useEffect(() => {
    if (!ready) return;
    const nextKey = fitKey ?? "__initial__";
    if (lastFitKey.current === nextKey) return;
    lastFitKey.current = nextKey;
    doFit();
  }, [ready, doFit, fitKey]);
  useEffect(() => {
    if (ready && flyToCenter) mapRef.current?.getMap?.().flyTo({ center: [flyToCenter[1], flyToCenter[0]], zoom: 12, duration: 900 });
  }, [ready, flyToCenter, flyToKey]);
  useEffect(() => { if (ready && focusedTrailId) focusTrail(focusedTrailId); }, [ready, focusedTrailId, trailFocusKey, focusTrail]);
  useEffect(() => {
    if (ready && eventLayerVisible && focusEvent) mapRef.current?.getMap?.().flyTo({ center: [focusEvent.lng, focusEvent.lat], zoom: 12, duration: 750 });
  }, [ready, eventLayerVisible, focusEvent]);

  const parkById = useMemo(() => {
    const map = {};
    parks.forEach((park) => { map[park.id] = park; });
    return map;
  }, [parks]);
  const selectedPark = selectedId != null ? parkById[selectedId] : null;

  const clusterIndex = useMemo(() => {
    if (!cluster) return null;
    const index = new Supercluster({ radius: 60, maxZoom: 12 });
    index.load(parks.map((park) => ({ type: "Feature", properties: { parkId: park.id }, geometry: { type: "Point", coordinates: [park.lng, park.lat] } })));
    return index;
  }, [cluster, parks]);
  const clusters = useMemo(() => {
    if (!cluster || !clusterIndex || !viewport.bounds) return [];
    return clusterIndex.getClusters(viewport.bounds, Math.round(viewport.zoom));
  }, [cluster, clusterIndex, viewport]);

  const eventIndex = useMemo(() => {
    if (!eventLayerVisible || !cluster) return null;
    const index = new Supercluster({ radius: 50, maxZoom: 13 });
    index.load(eventPins.filter((event) => event.id !== selectedEventId).map((event) => ({ type: "Feature", properties: { eventId: event.id }, geometry: { type: "Point", coordinates: [event.longitude, event.latitude] } })));
    return index;
  }, [cluster, eventLayerVisible, eventPins, selectedEventId]);
  const eventClusters = useMemo(() => {
    if (!eventIndex || !viewport.bounds) return [];
    return eventIndex.getClusters(viewport.bounds, Math.round(viewport.zoom));
  }, [eventIndex, viewport]);

  const syncViewport = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const bounds = map.getBounds();
    setViewport({ bounds: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()], zoom: map.getZoom() });
  }, []);
  const handleMoveEnd = useCallback((event) => {
    syncViewport();
    if (onViewportSettled && event.originalEvent) onViewportSettled({ lat: event.viewState.latitude, lng: event.viewState.longitude });
  }, [onViewportSettled, syncViewport]);
  const expandCluster = (id, lng, lat) => {
    const map = mapRef.current?.getMap?.();
    if (!map || !clusterIndex) return;
    const expansionZoom = Math.min(clusterIndex.getClusterExpansionZoom(id), 16);
    map.easeTo({ center: [lng, lat], zoom: Math.max(expansionZoom, map.getZoom() + 1), duration: 500 });
  };

  const zoomToPark = (park) => {
    const map = mapRef.current?.getMap?.();
    if (!map || !park) return;
    map.easeTo({
      center: [park.lng, park.lat],
      zoom: Math.max(12, Math.min(map.getZoom() + 2, 15)),
      duration: 550,
    });
  };
  const expandEventCluster = (id, lng, lat) => {
    const map = mapRef.current?.getMap?.();
    if (map && eventIndex) map.flyTo({ center: [lng, lat], zoom: Math.min(eventIndex.getClusterExpansionZoom(id), 17), duration: 600 });
  };
  const onMapMouseMove = (event) => {
    if (!onTrailHover) return;
    const feature = event.features?.[0];
    onTrailHover(feature ? feature.properties.id : null);
  };

  const renderParkMarker = (park) => {
    const state = selectedId === park.id ? "active" : hoveredId === park.id ? "hovered" : "";
    const showLabel = parks.length <= 12 || viewport.zoom >= 8;
    return (
      <Marker key={park.id} longitude={park.lng} latitude={park.lat} anchor="center">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSelect?.(park.id);
          }}
          onDoubleClick={(event) => {
            event.stopPropagation();
            zoomToPark(park);
          }}
          className="relative flex h-8 w-8 items-center justify-center"
          onMouseEnter={() => onHover && onHover(park.id)}
          onMouseLeave={() => onHover && onHover(null)}
          style={{ cursor: "pointer" }}
          data-testid="map-park-marker"
          aria-label={`Select ${park.name}. Double click to zoom.`}
        >
          {showLabel && (
            <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 max-w-[11rem] -translate-x-1/2 truncate rounded-md border border-white/10 bg-black/80 px-2.5 py-1 text-center text-[11px] font-bold text-white shadow-lg backdrop-blur">
              {park.name}
            </span>
          )}
          <div className={`map-marker ${state}`}><div className="map-marker-dot"/></div>
        </button>
      </Marker>
    );
  };

  if (!TOKEN) return <div className={`flex h-full w-full items-center justify-center bg-[#09090b] text-sm text-zinc-500 ${className}`}>{translateLabel("Map unavailable — missing Mapbox token")}</div>;

  return <div className={`bpf-map relative h-full w-full ${className}`}>
    <Map ref={mapRef} mapboxAccessToken={TOKEN} mapStyle={MAP_STYLE}
      initialViewState={{ longitude: center[1], latitude: center[0], zoom }} minZoom={minZoom}
      onLoad={() => { setReady(true); syncViewport(); }} onMove={cluster ? syncViewport : undefined}
      onMoveEnd={handleMoveEnd} interactiveLayerIds={trailFC ? ["trail-line"] : []}
      onMouseMove={onMapMouseMove} onClick={(event) => { const feature = event.features?.[0]; if (feature && onTrailSelect) onTrailSelect(feature.properties.id); }}
      cursor={hoveredTrailId ? "pointer" : "grab"} attributionControl={false} logoPosition="bottom-right" style={{ width: "100%", height: "100%" }}>
      {radiusFC && <Source id="radius" type="geojson" data={radiusFC}>
        <Layer id="radius-fill" type="fill" paint={{ "fill-color": "#CCFF00", "fill-opacity": 0.05 }}/>
        <Layer id="radius-line" type="line" paint={{ "line-color": "#CCFF00", "line-width": 1, "line-opacity": 0.4 }}/>
      </Source>}
      {trailFC && <Source id="trails" type="geojson" data={trailFC}>
        <Layer id="trail-casing" type="line" layout={{ "line-cap": "round", "line-join": "round" }} paint={{ "line-color": "#000", "line-width": 6, "line-opacity": 0.5 }}/>
        <Layer id="trail-line" type="line" layout={{ "line-cap": "round", "line-join": "round" }} paint={{ "line-color": ["get", "color"], "line-width": 3.5, "line-opacity": 0.9 }}/>
        <Layer id="trail-selected" type="line" layout={{ "line-cap": "round", "line-join": "round" }} filter={["==", ["get", "id"], selectedTrailId || "__none__"]} paint={{ "line-color": "#CCFF00", "line-width": 7, "line-opacity": 0.95 }}/>
        <Layer id="trail-highlight" type="line" layout={{ "line-cap": "round", "line-join": "round" }} filter={["==", ["get", "id"], hoveredTrailId || "__none__"]} paint={{ "line-color": ["get", "color"], "line-width": 6, "line-opacity": 1 }}/>
      </Source>}
      {origin && <Marker longitude={origin.lng} latitude={origin.lat}><div style={{ width: 18, height: 18, borderRadius: 999, background: "#fff", border: "4px solid #CCFF00", boxShadow: "0 0 0 4px rgba(204,255,0,0.25),0 0 14px rgba(204,255,0,0.5)" }}/></Marker>}
      {cluster && clusters.map((point) => {
        const [lng, lat] = point.geometry.coordinates;
        if (point.properties.cluster) {
          const count = point.properties.point_count;
          const size = 34 + Math.min(count, 8) * 3;
          return <Marker key={`cluster-${point.id}`} longitude={lng} latitude={lat}>
            <button
              type="button"
              aria-label={`${count} destinations. Zoom in`}
              onClick={(event) => {
                event.stopPropagation();
                expandCluster(point.id, lng, lat);
              }}
              data-testid="map-cluster"
              style={{ width: size, height: size, cursor: "pointer" }}
              className="flex items-center justify-center rounded-full border-2 border-[#CCFF00] bg-[#CCFF00]/20 font-mono text-sm font-bold text-[#CCFF00] backdrop-blur-md transition-transform hover:scale-110 active:scale-95"
            >
              <span className="flex h-full w-full items-center justify-center rounded-full bg-black/60">{count}</span>
            </button>
          </Marker>;
        }
        const park = parkById[point.properties.parkId];
        if (!park) return null;
        return renderParkMarker(park);
      })}
      {!cluster && parks.map(renderParkMarker)}
      {selectedParkOverlay && selectedPark && (
        <Marker
          key={`selected-preview-${selectedPark.id}`}
          longitude={selectedPark.lng}
          latitude={selectedPark.lat}
          anchor="bottom"
          style={{ zIndex: 35 }}
        >
          <div className="hidden md:block" data-testid="desktop-map-selected-park">
            {selectedParkOverlay}
          </div>
        </Marker>
      )}
      {eventLayerVisible && cluster && eventClusters.map((point) => {
        const [lng, lat] = point.geometry.coordinates;
        if (point.properties.cluster) return <Marker key={`event-cluster-${point.id}`} longitude={lng} latitude={lat} onClick={(event) => { event.originalEvent.stopPropagation(); expandEventCluster(point.id, lng, lat); }}>
          <button type="button" aria-label={`${point.properties.point_count} events. Zoom in`} data-testid="map-event-cluster" className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-violet-300 bg-violet-700/90 text-sm font-bold text-white shadow-lg shadow-violet-900/50">{point.properties.point_count}</button>
        </Marker>;
        const event = eventById.get(point.properties.eventId);
        if (!event) return null;
        return <Marker key={`event-${event.id}`} longitude={lng} latitude={lat} onClick={(click) => { click.originalEvent.stopPropagation(); selectEvent?.(event); }}>
          <button type="button" aria-label={`Event: ${event.title}`} data-testid="map-event-marker" className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-violet-600 text-white shadow-lg shadow-violet-900/70 hover:scale-110"><CalendarBlankIcon size={17} weight="bold"/></button>
        </Marker>;
      })}
      {eventLayerVisible && !cluster && eventPins.filter((event) => event.id !== selectedEventId).map((event) => <Marker key={`event-${event.id}`} longitude={event.longitude} latitude={event.latitude} onClick={(click) => { click.originalEvent.stopPropagation(); selectEvent?.(event); }}>
        <button type="button" aria-label={`Event: ${event.title}`} data-testid="map-event-marker" className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-violet-600 text-white shadow-lg shadow-violet-900/70 hover:scale-110"><CalendarBlankIcon size={17} weight="bold"/></button>
      </Marker>)}
      {eventLayerVisible && selectedEventId && eventById.has(selectedEventId) && <Marker key={`selected-event-${selectedEventId}`} longitude={eventById.get(selectedEventId).longitude} latitude={eventById.get(selectedEventId).latitude} style={{ zIndex: 30 }} onClick={(click) => { click.originalEvent.stopPropagation(); selectEvent?.(eventById.get(selectedEventId)); }}>
        <button type="button" aria-label={`Selected event: ${eventById.get(selectedEventId).title}`} data-testid="map-selected-event" className="flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-white bg-violet-500 text-white shadow-xl shadow-violet-700/80"><CalendarBlankIcon size={21} weight="fill"/></button>
      </Marker>}
    </Map>
    {selectedParkOverlay && (
      <div className="pointer-events-none absolute inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] z-[500] flex justify-center md:hidden">
        <div className="pointer-events-auto" data-testid="map-selected-park">{selectedParkOverlay}</div>
      </div>
    )}
    <div className="absolute bottom-4 right-4 z-[400] flex flex-col gap-2">
      <MapBtn onClick={() => mapRef.current?.getMap?.().zoomIn({ duration: 300 })} label={translateLabel("Zoom in")}><PlusIcon size={16} weight="bold"/></MapBtn>
      <MapBtn onClick={() => mapRef.current?.getMap?.().zoomOut({ duration: 300 })} label={translateLabel("Zoom out")}><MinusIcon size={16} weight="bold"/></MapBtn>
      {origin && <MapBtn onClick={doFit} label={translateLabel("Recenter")}><CrosshairSimpleIcon size={16} weight="bold"/></MapBtn>}
    </div>
  </div>;
}

function MapBtn({ children, onClick, label }) {
  return <button onClick={onClick} aria-label={label} data-testid={`map-${label.toLowerCase().replace(/\s/g, "-")}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-black/70 text-white backdrop-blur-md transition-colors hover:bg-black/90 active:scale-95">{children}</button>;
}
